import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import useTenantState from './tenant.state';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import useCompanyProfileCompletionState from './completion.state';

export interface IOrgChart {
  id: number;
  name: string;
  description?: string;
  is_published: boolean;
  tenant_id: number;
  organization_id?: number | null;
  parent_id?: number | null;
  created_at?: string;
  updated_at?: string;
  children?: IOrgChart[];
}

interface OrgChartState {
  orgCharts: IOrgChart[];
  isLoading: boolean;
  error: unknown;
  hasOrgChart: boolean;
  selectedOrganizationId: number | null;

  setSelectedOrganizationId: (id: number | null) => void;
  fetchOrgChartTree: (tenantId: string | number, organizationId?: number) => Promise<IOrgChart | null>;
  fetchOrgChartSubtree: (parentId: number) => Promise<IOrgChart | null>;
  createOrgChart: (formData: Partial<IOrgChart>) => Promise<IOrgChart | null>;
  createChildOrgChart: (parentId: number, formData: Partial<IOrgChart>) => Promise<IOrgChart | null>;
  createSiblingOrgChart: (
    siblingId: number,
    formData: Partial<IOrgChart>,
    position?: 'before' | 'after',
  ) => Promise<IOrgChart | null>;
  insertAboveOrgChart: (childId: number, formData: Partial<IOrgChart>) => Promise<IOrgChart | null>;
  updateOrgChart: (id: number, formData: Partial<IOrgChart>) => Promise<void>;
  deleteOrgChart: (id: number) => Promise<void>;
  clearOrgCharts: () => void;
}

const useOrgChartState = create<OrgChartState>()(
  persist(
    (set, get) => ({
      orgCharts: [],
      isLoading: false,
      error: null,
      hasOrgChart: false,
      selectedOrganizationId: null,

      setSelectedOrganizationId: id => set({ selectedOrganizationId: id }),

      clearOrgCharts: () => set({ orgCharts: [] }),

      fetchOrgChartTree: async (tenantId, organizationId) => {
        set({ isLoading: true });
        try {
          const targetOrgId = organizationId ?? get().selectedOrganizationId;
          let url = `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${tenantId}/tree`;
          if (targetOrgId) {
            url += `?organization_id=${targetOrgId}`;
          }

          const { data } = await axiosInstance.get(url);

          set({
            orgCharts: Array.isArray(data) ? data : [data],
            isLoading: false,
            hasOrgChart: Array.isArray(data) ? data.length > 0 : !!data,
          });

          return data;
        } catch (error: any) {
          const status = error?.response?.status;

          if (status === 404) {
            set({
              orgCharts: [],
              isLoading: false,
              hasOrgChart: false,
              error: null,
            });
            return null;
          }

          errorHandler(error);
          set({
            orgCharts: [],
            isLoading: false,
            hasOrgChart: false,
            error,
          });
          return null;
        }
      },

      fetchOrgChartSubtree: async parentId => {
        set({ isLoading: true });
        try {
          const url = `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${parentId}/subtree`;
          const { data } = await axiosInstance.get(url);

          set({ isLoading: false });
          return data;
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
          return null;
        }
      },

      createOrgChart: async formData => {
        const tenantId = useTenantState.getState().currentTenant?.id;
        if (!tenantId) return null;

        set({ isLoading: true });
        try {
          const payload = {
            name: formData.name,
            description: formData.description,
            is_published: formData.is_published ?? true,
            tenant_id: tenantId,
            organization_id: formData.organization_id ?? null,
            parent_id: formData.parent_id ?? null,
          };

          const { data } = await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart`, payload);

          const completionStore = useCompanyProfileCompletionState.getState();
          completionStore.fetchCompletion();
          toast.success('Parent Org Chart created successfully');
          set({ isLoading: false });
          return data as IOrgChart;
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
          return null;
        }
      },

      createChildOrgChart: async (parentId, formData) => {
        const tenantId = useTenantState.getState().currentTenant?.id;
        if (!tenantId) return null;

        set({ isLoading: true });
        try {
          const payload = {
            name: formData.name,
            description: formData.description,
            is_published: formData.is_published ?? true,
            tenant_id: tenantId,
            organization_id: formData.organization_id ?? null,
            parent_id: parentId,
          };

          const { data } = await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart`, payload);

          const completionStore = useCompanyProfileCompletionState.getState();
          completionStore.fetchCompletion();
          toast.success('Child Org Chart created successfully');
          set({ isLoading: false });
          return data as IOrgChart;
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
          return null;
        }
      },

      createSiblingOrgChart: async (siblingId, formData, position = 'after') => {
        const tenantId = useTenantState.getState().currentTenant?.id;
        if (!tenantId) return null;

        set({ isLoading: true });
        try {
          const payload = {
            name: formData.name,
            description: formData.description,
            is_published: formData.is_published ?? true,
            tenant_id: tenantId,
            organization_id: formData.organization_id ?? null,
            position,
          };

          const { data } = await axiosInstance.post(
            `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${siblingId}/siblings`,
            payload,
          );

          const completionStore = useCompanyProfileCompletionState.getState();
          completionStore.fetchCompletion();
          toast.success('Sibling Org Chart created successfully');
          set({ isLoading: false });
          return data as IOrgChart;
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
          return null;
        }
      },

      insertAboveOrgChart: async (childId, formData) => {
        const tenantId = useTenantState.getState().currentTenant?.id;
        if (!tenantId) return null;

        set({ isLoading: true });
        try {
          const payload = {
            name: formData.name,
            description: formData.description,
            is_published: formData.is_published ?? true,
            tenant_id: tenantId,
            organization_id: formData.organization_id ?? null,
          };

          const { data } = await axiosInstance.post(
            `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${childId}/parent`,
            payload,
          );
          toast.success('Parent node created successfully');
          set({ isLoading: false });
          return data as IOrgChart;
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
          return null;
        }
      },

      updateOrgChart: async (id, formData) => {
        const tenantId = useTenantState.getState().currentTenant?.id;
        if (!tenantId) return;

        set({ isLoading: true });
        try {
          const payload = {
            name: formData.name,
            description: formData.description,
            is_published: formData.is_published ?? true,
            tenant_id: tenantId,
            organization_id: formData.organization_id ?? null,
            parent_id: formData.parent_id ?? null,
          };

          await axiosInstance.put(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${id}`, payload);
          toast.success('Org Chart updated successfully');
          set({ isLoading: false });
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
        }
      },

      deleteOrgChart: async id => {
        const tenantId = useTenantState.getState().currentTenant?.id;
        if (!tenantId) return;

        set({ isLoading: true });
        try {
          await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${id}`);
          const completionStore = useCompanyProfileCompletionState.getState();
          completionStore.fetchCompletion();
          toast.success('Org Chart deleted successfully');
          await get().fetchOrgChartTree(tenantId);
        } catch (err) {
          errorHandler(err);
          set({ isLoading: false, error: err });
        }
      },
    }),
    {
      name: 'org-chart-storage',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export default useOrgChartState;
